/**
 * Lead Finder: describe who you are looking for (the AI sets the search up and
 * runs it, or fills the form to review first), or set it up yourself —
 * keywords, map categories, an area picked on the Dislix map, sources, links,
 * the fields every result must have and the platforms wanted. Runs happen in
 * the background (the worker); this screen lists them with their progress and
 * the plan's monthly usage.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  ScrollView,
  TextInput,
  RefreshControl,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, FormHeader } from '../components';
import { Button, Toggle } from '../components/design-system';
import { ZoneMapPicker, ZoneResult } from '../components/ZoneMapPicker';
import { AiBotIcon } from '../components/icons/AppIcon';
import { LeadSearchSuggestions } from '../components/lead-finder/LeadSearchSuggestions';
import {
  ALL_PLATFORMS,
  Chip,
  PLATFORM_ICONS,
  ProgressBar,
  StatusBadge,
  categoryLabel,
  isActiveRun,
  notInPlan,
  PHASE_KEYS,
  regionFromZone,
  regionLabel,
  statusColor,
  takePendingClone,
} from '../components/lead-finder/leadFinderUi';
import {
  apiService,
  LeadAllowance,
  LeadField,
  LeadPhoneType,
  LeadPlatform,
  LeadRegion,
  LeadRun,
  LeadRunInput,
  LeadSource,
  LeadUsage,
  LeadTargetType,
  LeadRequestedField,
  LeadSearchSuggestion,
} from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useGoBack } from '../hooks/useGoBack';
import { useThemedStyles } from '../hooks/useThemedStyles';
import type { TranslationKey } from '../i18n/en';

const POLL_MS = 5000;
const FIELDS: LeadField[] = ['name', 'phone', 'location', 'email', 'website', 'description'];
const SOURCES: LeadSource[] = ['map', 'web', 'google_maps', 'links'];
const PHONE_TYPES: LeadPhoneType[] = ['any', 'mobile', 'landline'];
const SOURCE_ICONS: Record<LeadSource, keyof typeof Ionicons.glyphMap> = { map: 'map-outline', web: 'globe-outline', google_maps: 'logo-google', links: 'link-outline' };

const splitList = (text: string) => text.split(/[,\n،]/).map((s) => s.trim()).filter(Boolean);

interface FormState {
  targetType: LeadTargetType;
  professions: string;
  excludeKeywords: string;
  requestedFields: LeadRequestedField[];
  minScore: string;
  evaluateWithAi: boolean;
  name: string;
  brief: string;
  keywords: string;
  categories: string[];
  region: LeadRegion | null;
  sources: LeadSource[];
  links: string;
  required: LeadField[];
  phoneType: LeadPhoneType;
  platforms: LeadPlatform[];
  enrich: boolean;
  limit: string;
  /** Set when the form was opened from "Duplicate". */
  clonedFrom?: string;
}

const EMPTY_FORM: FormState = {
  targetType: 'any', professions: '', excludeKeywords: '', requestedFields: [], minScore: '50', evaluateWithAi: true,
  name: '',
  brief: '',
  keywords: '',
  categories: [],
  region: null,
  sources: ['map'],
  links: '',
  required: ['name', 'phone', 'location'],
  phoneType: 'any',
  platforms: [],
  enrich: true,
  limit: '100',
};

/** A run's settings as the form ("Duplicate"). */
const formFromRun = (run: LeadRun): FormState => {
  const p = run.params || ({} as LeadRun['params']);
  return {
    targetType: p.targetType || 'any', professions: (p.professions || []).join(', '), excludeKeywords: (p.excludeKeywords || []).join(', '),
    requestedFields: p.requestedFields || [], minScore: String(p.minScore ?? 50), evaluateWithAi: p.evaluateWithAi !== false,
    name: run.name,
    brief: run.brief || '',
    keywords: (p.keywords || []).join(', '),
    categories: p.categories || [],
    region: p.region || null,
    sources: p.sources?.length ? p.sources : ['map'],
    links: (p.links || []).join('\n'),
    required: p.required?.length ? p.required : EMPTY_FORM.required,
    phoneType: p.phoneType || 'any',
    platforms: p.platforms || [],
    enrich: p.enrich !== false,
    limit: String(p.limit || 100),
    clonedFrom: run.id,
  };
};

export const LeadFinderScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, activeTab, setActiveTab, showNotification } = useAppContext();
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);
  const goBack = useGoBack('home');
  const { t } = useLocale();
  const rtl = useRTLStyles();

  const [runs, setRuns] = useState<LeadRun[]>([]);
  const [total, setTotal] = useState(0);
  const [allowance, setAllowance] = useState<LeadAllowance | null>(null);
  const [usage, setUsage] = useState<LeadUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);

  const [brief, setBrief] = useState('');
  const [aiReady, setAiReady] = useState(false);
  const [fieldLabel, setFieldLabel] = useState('');
  const [aiBusy, setAiBusy] = useState<'plan' | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [aiPlanned, setAiPlanned] = useState(false);
  const [starting, setStarting] = useState(false);
  const [showMap, setShowMap] = useState(false);

  const [categories, setCategories] = useState<string[]>([]);
  const [categorySearch, setCategorySearch] = useState('');
  useEffect(() => {
    let alive = true;
    apiService.getAiStatus().then(res => {
      if (!alive) return;
      const ready = !!(res.data?.entitled && res.data?.configured);
      setAiReady(ready);
      if (!ready) { setShowForm(true); setForm(f => ({ ...f, evaluateWithAi: false })); }
    }).catch(() => { if (alive) { setShowForm(true); setForm(f => ({ ...f, evaluateWithAi: false })); } });
    return () => { alive = false; };
  }, []);

  // Opened from a run's "Duplicate": start from its settings.
  useEffect(() => {
    const clone = takePendingClone();
    if (clone) {
      setForm(formFromRun(clone));
      setAiPlanned(!!clone.aiPlanned);
      setShowForm(true);
    }
  }, []);

  const scrollRef = useRef<ScrollView>(null);
  const [rerunning, setRerunning] = useState<string | null>(null);
  const rerun = async (run: LeadRun) => {
    setRerunning(run.id);
    try {
      const res = await apiService.rerunLeadRun(run.id, true);
      if (res.statusCode === 1000 && res.data) {
        showNotification('success', t('leadFinder.title'), t('leadFinder.rerun.started'));
        navigateTo('lead-run', res.data.id);
      } else failure(res, 'leadFinder.error.start');
    } catch (err) {
      failure(err, 'leadFinder.error.start');
    } finally {
      setRerunning(null);
    }
  };

  const loadRuns = useCallback(async (quiet = false) => {
    try {
      if (!quiet) setError(null);
      const res = await apiService.getLeadRuns({ limit: 50 });
      if (res.statusCode === 1000 && res.data) {
        setRuns(res.data.items || []);
        setTotal(res.data.total || 0);
        setAllowance(res.data.allowance);
        setUsage(res.data.usage);
        setLocked(!res.data.allowance?.enabled);
      } else if (notInPlan(res)) {
        setLocked(true);
      } else if (!quiet) {
        setError(res.errors?.[0]?.message || t('leadFinder.error.load'));
      }
    } catch (err: any) {
      if (notInPlan(err)) setLocked(true);
      else if (!quiet) setError(err?.message || t('leadFinder.error.load'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t]);

  useEffect(() => {
    loadRuns();
    apiService.getLeadFinderCategories()
      .then((res) => { if (res.statusCode === 1000 && res.data) setCategories(res.data.categories || []); })
      .catch(() => {});
  }, [loadRuns]);

  // Follow running searches.
  const hasActive = runs.some((r) => isActiveRun(r.status));
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (!hasActive) return;
    pollRef.current = setInterval(() => loadRuns(true), POLL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [hasActive, loadRuns]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));
  const toggle = <T,>(list: T[], value: T) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

  const failure = (res: any, fallback: TranslationKey) => {
    showNotification('error', t('leadFinder.title'), res?.errors?.[0]?.message || res?.message || t(fallback));
  };

  const started = (run: LeadRun) => {
    showNotification('success', t('leadFinder.title'), t('leadFinder.started', { name: run.name }));
    setBrief('');
    setForm(EMPTY_FORM);
    setShowForm(false);
    setAiPlanned(false);
    navigateTo('lead-run', run.id);
  };

  /** "Review first": the AI fills the form. */
  const planWithAi = async () => {
    if (brief.trim().length < 3) return;
    setAiBusy('plan');
    try {
      const res = await apiService.planLeadRun(brief.trim(), form.region);
      if (res.statusCode === 1000 && res.data) {
        const p = res.data;
        setForm((f) => ({
          ...f,
          targetType: p.targetType || 'any', professions: (p.professions || []).join(', '), excludeKeywords: (p.excludeKeywords || []).join(', '),
          requestedFields: p.requestedFields || [], minScore: String(p.minScore ?? 50), evaluateWithAi: true,
          name: p.name || f.name,
          brief: brief.trim(),
          keywords: (p.keywords || []).join(', '),
          categories: p.categories || [],
          region: p.region || f.region,
          // The AI's sources (people → web), shown so what runs is what the form says; still editable.
          sources: p.sources?.length ? p.sources : f.sources,
          required: p.required?.length ? p.required : f.required,
          phoneType: p.phoneType || 'any',
          platforms: p.platforms || [],
          limit: p.limit ? String(p.limit) : f.limit,
        }));
        setAiPlanned(true);
        setShowForm(true);
      } else failure(res, 'leadFinder.error.plan');
    } catch (err) {
      failure(err, 'leadFinder.error.plan');
    } finally {
      setAiBusy(null);
    }
  };

  const startSearch = async () => {
    const body: LeadRunInput & { aiPlanned?: boolean } = {
      targetType: form.targetType, professions: splitList(form.professions), excludeKeywords: splitList(form.excludeKeywords),
      requestedFields: form.requestedFields, minScore: Math.max(0, Math.min(100, Number(form.minScore) || 0)), evaluateWithAi: aiReady && form.evaluateWithAi,
      name: form.name.trim() || undefined,
      brief: form.brief || undefined,
      keywords: splitList(form.keywords),
      categories: form.categories,
      region: form.region,
      sources: form.sources,
      links: form.sources.includes('links') ? splitList(form.links) : [],
      required: form.required,
      phoneType: form.required.includes('phone') ? form.phoneType : 'any',
      platforms: form.platforms,
      enrich: form.enrich,
      limit: Math.max(1, Number(form.limit) || 100),
      aiPlanned,
      ...(form.clonedFrom ? { clonedFrom: form.clonedFrom } : {}),
    };
    setStarting(true);
    try {
      const res = await apiService.createLeadRun(body);
      if (res.statusCode === 1000 && res.data) started(res.data);
      else failure(res, 'leadFinder.error.start');
    } catch (err) {
      failure(err, 'leadFinder.error.start');
    } finally {
      setStarting(false);
    }
  };

  const onZone = (zone: ZoneResult) => {
    setShowMap(false);
    set('region', regionFromZone(zone));
  };

  const categoryMatches = useMemo(() => {
    const q = categorySearch.trim().toLowerCase().replace(/\s+/g, '_');
    if (!q) return [];
    return categories.filter((c) => c.includes(q) && !form.categories.includes(c)).slice(0, 12);
  }, [categorySearch, categories, form.categories]);

  const usageLine = () => {
    if (!allowance || !usage) return undefined;
    const runsText = allowance.runsPerMonth === null
      ? t('leadFinder.usage.runsUnlimited', { used: usage.runs })
      : t('leadFinder.usage.runs', { used: usage.runs, limit: allowance.runsPerMonth });
    const resultsText = allowance.resultsPerMonth === null
      ? t('leadFinder.usage.resultsUnlimited', { used: usage.results })
      : t('leadFinder.usage.results', { used: usage.results, limit: allowance.resultsPerMonth });
    return `${runsText} · ${resultsText}`;
  };

  // ── Pieces ────────────────────────────────────────────────────────────────

  const renderUsage = () => {
    if (!allowance || !usage) return null;
    const bars: Array<{ label: string; used: number; limit: number | null }> = [
      { label: t('leadFinder.usage.runsLabel'), used: usage.runs, limit: allowance.runsPerMonth },
      { label: t('leadFinder.usage.resultsLabel'), used: usage.results, limit: allowance.resultsPerMonth },
    ];
    return (
      <View style={styles.card}>
        <View style={[styles.rowBetween, { flexDirection: rtl.rowDirection }]}>
          <Text style={styles.cardTitle}>{t('leadFinder.usage.title')}</Text>
          {allowance.resultsPerRun !== null ? (
            <Text style={styles.meta}>{t('leadFinder.usage.perRun', { limit: allowance.resultsPerRun })}</Text>
          ) : null}
        </View>
        {bars.map((b) => (
          <View key={b.label} style={styles.usageRow}>
            <View style={[styles.rowBetween, { flexDirection: rtl.rowDirection }]}>
              <Text style={styles.meta}>{b.label}</Text>
              <Text style={styles.meta}>{b.limit === null ? `${b.used}` : `${b.used} / ${b.limit}`}</Text>
            </View>
            {b.limit !== null ? (
              <ProgressBar value={b.limit ? b.used / b.limit : 1} color={b.limit && b.used >= b.limit ? (colors.status.error as string) : undefined} />
            ) : null}
          </View>
        ))}
        {!allowance.googleMaps ? <Text style={styles.hint}>{t('leadFinder.usage.noGoogle')}</Text> : null}
        {!allowance.web ? <Text style={styles.hint}>{t('leadFinder.usage.noWeb')}</Text> : null}
      </View>
    );
  };

  const renderRegion = () => (
    <View style={[styles.wrapRow, { flexDirection: rtl.rowDirection }]}>
      {form.region ? (
        <Chip label={regionLabel(form.region) || t('leadFinder.form.regionPicked')} icon="location-outline" selected onRemove={() => set('region', null)} onPress={() => setShowMap(true)} />
      ) : null}
      <Chip label={form.region ? t('leadFinder.form.regionChange') : t('leadFinder.form.regionPick')} icon="map-outline" onPress={() => setShowMap(true)} />
    </View>
  );

  const reviewSuggestion = (suggestion: LeadSearchSuggestion) => {
    const p = suggestion.settings;
    setBrief(suggestion.prompt);
    setForm(f => ({ ...EMPTY_FORM,
      name: p.name || suggestion.title, brief: suggestion.prompt,
      targetType: p.targetType || 'any', professions: (p.professions || []).join(', '),
      excludeKeywords: (p.excludeKeywords || []).join(', '), requestedFields: p.requestedFields || [],
      minScore: String(p.minScore ?? 50), evaluateWithAi: true,
      keywords: (p.keywords || []).join(', '), categories: p.categories || [],
      region: f.region || p.region || null, sources: p.sources?.length ? p.sources : ['map'],
      required: p.required?.length ? p.required : ['name'], phoneType: p.phoneType || 'any',
      platforms: p.platforms || [], enrich: p.enrich !== false, limit: String(p.limit || 100),
    }));
    setAiPlanned(true); setShowForm(true);
  };

  const renderAiBox = () => aiReady ? (
    <View style={styles.card}>
      <View style={[styles.aiTitleRow, { flexDirection: rtl.rowDirection }]}>
        <AiBotIcon size={20} color={colors.primary as string} />
        <Text style={styles.cardTitle}>{t('leadFinder.ai.title')}</Text>
      </View>
      <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{t('leadFinder.ai.hint')}</Text>
      <LeadSearchSuggestions onReview={reviewSuggestion} />
      <TextInput
        value={brief}
        onChangeText={setBrief}
        placeholder={t('leadFinder.ai.placeholder')}
        placeholderTextColor={colors.text.secondary as string}
        style={[styles.input, styles.multiline, { textAlign: rtl.textStart }]}
        multiline
        maxLength={1500}
      />
      {renderRegion()}
      <View style={[styles.buttonRow, { flexDirection: rtl.rowDirection }]}>
        <Button
          title={t('leadFinder.ai.plan')}
          onPress={planWithAi}
          variant="primary"
          loading={aiBusy === 'plan'}
          disabled={!!aiBusy || brief.trim().length < 3}
          style={styles.flexButton}
        />
      </View>
      {!showForm ? (
        <TouchableOpacity onPress={() => setShowForm(true)} style={styles.linkButton}>
          <Text style={styles.linkText}>{t('leadFinder.form.manual')}</Text>
        </TouchableOpacity>
      ) : null}
    </View>
  ) : <View style={styles.card}>
    <Text style={styles.hint}>{t('leadFinder.criteria.noAi')}</Text>
    {!showForm ? <Button title={t('leadFinder.form.manual')} onPress={() => setShowForm(true)} variant="primary" /> : null}
  </View>;

  const renderForm = () => (
    <View style={styles.card}>
      <View style={[styles.rowBetween, { flexDirection: rtl.rowDirection }]}>
        <Text style={styles.cardTitle}>{form.clonedFrom ? t('leadFinder.form.duplicateTitle') : aiPlanned ? t('leadFinder.form.reviewTitle') : t('leadFinder.form.title')}</Text>
        <TouchableOpacity onPress={() => { setShowForm(false); setAiPlanned(false); }} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
          <Ionicons name="close" size={20} color={colors.text.secondary as string} />
        </TouchableOpacity>
      </View>

      <Text style={styles.label}>{t('leadFinder.form.name')}</Text>
      <TextInput value={form.name} onChangeText={(v) => set('name', v)} placeholder={t('leadFinder.form.namePlaceholder')} placeholderTextColor={colors.text.secondary as string} style={[styles.input, { textAlign: rtl.textStart }]} maxLength={120} />

      <Text style={styles.label}>{t('leadFinder.criteria.target')}</Text>
      <View style={[styles.wrapRow, { flexDirection: rtl.rowDirection }]}>
        {(['any', 'entity', 'individual'] as const).map(type => <Chip key={type} label={t(`leadFinder.target.${type}` as TranslationKey)} selected={form.targetType === type} onPress={() => set('targetType', type)} />)}
      </View>
      {form.targetType === 'individual' ? <Text style={styles.hint}>{t('leadFinder.criteria.individualHint')}</Text> : null}
      <Text style={styles.label}>{t('leadFinder.criteria.purpose')}</Text>
      <TextInput value={form.brief} onChangeText={value => set('brief', value)} multiline maxLength={1500} style={[styles.input, styles.multiline, { textAlign: rtl.textStart }]} />
      <Text style={styles.label}>{t('leadFinder.criteria.professions')}</Text>
      <TextInput value={form.professions} onChangeText={value => set('professions', value)} placeholder={t('leadFinder.criteria.professionsPlaceholder')} placeholderTextColor={colors.text.secondary as string} style={[styles.input, { textAlign: rtl.textStart }]} />
      <Text style={styles.label}>{t('leadFinder.criteria.exclude')}</Text>
      <TextInput value={form.excludeKeywords} onChangeText={value => set('excludeKeywords', value)} style={[styles.input, { textAlign: rtl.textStart }]} />
      <Text style={styles.label}>{t('leadFinder.criteria.minimum')}</Text>
      <TextInput value={form.minScore} onChangeText={value => set('minScore', value)} keyboardType="numeric" maxLength={3} style={styles.input} />
      {aiReady ? <Toggle value={form.evaluateWithAi} onValueChange={value => set('evaluateWithAi', value)} label={t('leadFinder.criteria.useAi')} description={t('leadFinder.criteria.evaluationHint')} /> : null}

      <Text style={styles.label}>{t('leadFinder.criteria.fields')}</Text>
      <Text style={styles.hint}>{t('leadFinder.criteria.fieldsHint')}</Text>
      {form.requestedFields.map((field, index) => (
        <View key={field.key} style={{ gap: spacing.xs }}>
          <View style={[styles.wrapRow, { flexDirection: rtl.rowDirection }]}>
            <Text style={styles.label}>{field.label}</Text>
            {(['text', 'number', 'boolean'] as const).map(type => <Chip key={type} label={t(`leadFinder.criteria.type.${type}` as TranslationKey)} selected={field.type === type} onPress={() => set('requestedFields', form.requestedFields.map((f, i) => i === index ? { ...f, type } : f))} />)}
            <Chip label={t('leadFinder.criteria.remove')} icon="close" onPress={() => set('requestedFields', form.requestedFields.filter((_, i) => i !== index))} />
          </View>
          <Toggle value={field.required} label={t('leadFinder.criteria.required')} onValueChange={value => set('requestedFields', form.requestedFields.map((f, i) => i === index ? { ...f, required: value } : f))} />
          <TextInput value={field.expected || ''} placeholder={t('leadFinder.criteria.expected')} placeholderTextColor={colors.text.secondary as string} onChangeText={value => set('requestedFields', form.requestedFields.map((f, i) => i === index ? { ...f, expected: value } : f))} style={[styles.input, { textAlign: rtl.textStart }]} />
        </View>
      ))}
      <TextInput value={fieldLabel} onChangeText={setFieldLabel} placeholder={t('leadFinder.criteria.fieldPlaceholder')} placeholderTextColor={colors.text.secondary as string} style={[styles.input, { textAlign: rtl.textStart }]} />
      <Button title={t('leadFinder.criteria.addField')} variant="outline" size="sm" disabled={!fieldLabel.trim() || form.requestedFields.length >= 20} onPress={() => {
        const label = fieldLabel.trim().slice(0, 80);
        const key = label.normalize('NFKC').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '_').replace(/^_|_$/g, '').slice(0, 60);
        if (key && !form.requestedFields.some(f => f.key === key)) set('requestedFields', [...form.requestedFields, { key, label, type: 'text', required: false }]);
        setFieldLabel('');
      }} />

      <Text style={styles.label}>{t('leadFinder.form.keywords')}</Text>
      <TextInput value={form.keywords} onChangeText={(v) => set('keywords', v)} placeholder={t('leadFinder.form.keywordsPlaceholder')} placeholderTextColor={colors.text.secondary as string} style={[styles.input, { textAlign: rtl.textStart }]} />

      <Text style={styles.label}>{t('leadFinder.form.categories')}</Text>
      {form.categories.length ? (
        <View style={[styles.wrapRow, { flexDirection: rtl.rowDirection }]}>
          {form.categories.map((c) => <Chip key={c} label={categoryLabel(c)} selected onRemove={() => set('categories', form.categories.filter((x) => x !== c))} />)}
        </View>
      ) : null}
      <TextInput value={categorySearch} onChangeText={setCategorySearch} placeholder={t('leadFinder.form.categoriesPlaceholder')} placeholderTextColor={colors.text.secondary as string} style={[styles.input, { textAlign: rtl.textStart }]} />
      {categoryMatches.length ? (
        <View style={[styles.wrapRow, { flexDirection: rtl.rowDirection }]}>
          {categoryMatches.map((c) => (
            <Chip key={c} label={categoryLabel(c)} icon="add" onPress={() => { set('categories', [...form.categories, c].slice(0, 12)); setCategorySearch(''); }} />
          ))}
        </View>
      ) : null}

      <Text style={styles.label}>{t('leadFinder.form.region')}</Text>
      {renderRegion()}

      <Text style={styles.label}>{t('leadFinder.form.sources')}</Text>
      <View style={[styles.wrapRow, { flexDirection: rtl.rowDirection }]}>
        {SOURCES.map((s) => {
          const lockedSource = allowance && ((s === 'google_maps' && !allowance.googleMaps) || (s === 'web' && !allowance.web));
          return (
            <Chip
              key={s}
              label={t(`leadFinder.source.${s}` as TranslationKey) + (lockedSource ? ` · ${t('leadFinder.form.notInPlan')}` : '')}
              icon={lockedSource ? 'lock-closed-outline' : SOURCE_ICONS[s]}
              selected={form.sources.includes(s)}
              disabled={!!lockedSource}
              onPress={() => set('sources', toggle(form.sources, s))}
            />
          );
        })}
      </View>
      {form.sources.includes('links') ? (
        <>
          <Text style={styles.label}>{t('leadFinder.form.links')}</Text>
          <TextInput value={form.links} onChangeText={(v) => set('links', v)} placeholder={t('leadFinder.form.linksPlaceholder')} placeholderTextColor={colors.text.secondary as string} style={[styles.input, styles.multiline, { textAlign: rtl.textStart }]} multiline autoCapitalize="none" />
        </>
      ) : null}

      <Text style={styles.label}>{t('leadFinder.form.required')}</Text>
      <View style={[styles.wrapRow, { flexDirection: rtl.rowDirection }]}>
        {FIELDS.map((f) => (
          <Chip
            key={f}
            label={t(`leadFinder.field.${f}` as TranslationKey)}
            selected={form.required.includes(f)}
            disabled={f === 'name'}
            onPress={() => set('required', toggle(form.required, f))}
          />
        ))}
      </View>

      {form.required.includes('phone') ? (
        <>
          <Text style={styles.label}>{t('leadFinder.form.phoneType')}</Text>
          <View style={[styles.wrapRow, { flexDirection: rtl.rowDirection }]}>
            {PHONE_TYPES.map((pt) => (
              <Chip
                key={pt}
                label={t(`leadFinder.phoneType.${pt}` as TranslationKey)}
                icon={pt === 'mobile' ? 'phone-portrait-outline' : pt === 'landline' ? 'call-outline' : undefined}
                selected={form.phoneType === pt}
                onPress={() => set('phoneType', pt)}
              />
            ))}
          </View>
        </>
      ) : null}

      <Text style={styles.label}>{t('leadFinder.form.platforms')}</Text>
      <Text style={styles.hint}>{t('leadFinder.form.platformsHint')}</Text>
      <View style={[styles.wrapRow, { flexDirection: rtl.rowDirection }]}>
        {ALL_PLATFORMS.map((p) => (
          <Chip key={p} label={t(`leadFinder.platform.${p}` as TranslationKey)} icon={PLATFORM_ICONS[p]} selected={form.platforms.includes(p)} onPress={() => set('platforms', toggle(form.platforms, p))} />
        ))}
      </View>

      <View style={styles.toggleRow}>
        <Toggle value={form.enrich} onValueChange={(v) => set('enrich', v)} label={t('leadFinder.form.enrich')} description={t('leadFinder.form.enrichHint')} />
      </View>

      <Text style={styles.label}>{t('leadFinder.form.limit')}</Text>
      <TextInput value={form.limit} onChangeText={(v) => set('limit', v.replace(/\D/g, ''))} keyboardType="number-pad" style={[styles.input, styles.limitInput, { textAlign: rtl.textStart }]} maxLength={4} />
      {allowance?.resultsPerRun != null ? <Text style={styles.hint}>{t('leadFinder.usage.perRun', { limit: allowance.resultsPerRun })}</Text> : null}

      <Button title={t('leadFinder.form.start')} onPress={startSearch} variant="primary" loading={starting} disabled={starting} style={styles.startButton} />
    </View>
  );

  const renderRun = (run: LeadRun) => {
    const p = run.progress || {};
    const active = isActiveRun(run.status);
    return (
      <TouchableOpacity key={run.id} style={styles.card} activeOpacity={0.7} onPress={() => navigateTo('lead-run', run.id)}>
        <View style={[styles.runHeader, { flexDirection: rtl.rowDirection }]}>
          <View style={styles.runIcon}>
            {run.aiPlanned ? <AiBotIcon size={20} color={colors.primary as string} /> : <Ionicons name="search-outline" size={20} color={colors.primary as string} />}
          </View>
          <View style={styles.flex}>
            <Text style={[styles.cardTitle, { textAlign: rtl.textStart }]} numberOfLines={1}>{run.name}</Text>
            <Text style={[styles.meta, { textAlign: rtl.textStart }]} numberOfLines={1}>
              {[regionLabel(run.params?.region), new Date(run.createdAt).toLocaleString(), run.origin === 'workflow' ? t('leadFinder.origin.workflow') : null].filter(Boolean).join(' · ')}
            </Text>
          </View>
          <StatusBadge label={run.cancelRequested && run.status === 'running' ? t('leadFinder.run.stopping') : t(`leadFinder.status.${run.status}` as TranslationKey)} color={statusColor(run.status)} />
        </View>
        {active ? (
          <View style={styles.usageRow}>
            <ProgressBar value={run.allowance ? (p.kept || 0) / run.allowance : 0} />
            <Text style={styles.meta}>{run.phase && PHASE_KEYS[run.phase] ? t(PHASE_KEYS[run.phase]) : t(`leadFinder.status.${run.status}` as TranslationKey)}</Text>
          </View>
        ) : null}
        <Text style={[styles.meta, styles.runStats, { textAlign: rtl.textStart }]}>
          {t('leadFinder.progress', { kept: p.kept || 0, found: p.found || 0, duplicates: p.duplicates || 0, existing: p.existing || 0 })}
        </Text>
        <View style={[styles.runActions, { flexDirection: rtl.rowDirection }]}>
          {!active ? (
            <TouchableOpacity style={[styles.runAction, { flexDirection: rtl.rowDirection }]} onPress={() => rerun(run)} disabled={!!rerunning}>
              {rerunning === run.id ? <ActivityIndicator size="small" color={colors.primary as string} /> : <Ionicons name="refresh-outline" size={15} color={colors.primary as string} />}
              <Text style={styles.runActionText}>{t('leadFinder.rerun.action')}</Text>
            </TouchableOpacity>
          ) : null}
            <TouchableOpacity
              style={[styles.runAction, { flexDirection: rtl.rowDirection }]}
              onPress={() => { setForm(formFromRun(run)); setAiPlanned(!!run.aiPlanned); setShowForm(true); scrollRef.current?.scrollTo({ y: 0, animated: true }); }}
            >
              <Ionicons name="copy-outline" size={15} color={colors.primary as string} />
              <Text style={styles.runActionText}>{t('leadFinder.duplicate.action')}</Text>
            </TouchableOpacity>
        </View>
      </TouchableOpacity>
    );
  };

  const renderBody = () => {
    if (loading) {
      return (
        <View style={styles.center}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      );
    }
    if (locked) {
      return (
        <View style={styles.center}>
          <Ionicons name="lock-closed-outline" size={56} color={colors.text.secondary as string} />
          <Text style={styles.emptyTitle}>{t('leadFinder.locked.title')}</Text>
          <Text style={styles.emptyText}>{t('leadFinder.locked.message')}</Text>
          <Button title={t('leadFinder.locked.upgrade')} onPress={() => navigateTo('subscription')} variant="primary" style={styles.centerButton} />
        </View>
      );
    }
    if (error) {
      return (
        <View style={styles.center}>
          <Ionicons name="alert-circle-outline" size={56} color={colors.error} />
          <Text style={styles.emptyText}>{error}</Text>
          <Button title={t('common.retry')} onPress={() => { setLoading(true); loadRuns(); }} variant="primary" style={styles.centerButton} />
        </View>
      );
    }
    return (
      <ScrollView
        ref={scrollRef}
        contentContainerStyle={styles.content}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); loadRuns(); }} colors={[colors.primary as string]} />}
        keyboardShouldPersistTaps="handled"
      >
        {renderAiBox()}
        {showForm ? renderForm() : null}
        {renderUsage()}
        <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('leadFinder.runs.title')}</Text>
        {runs.length ? runs.map(renderRun) : <Text style={[styles.emptyText, styles.emptyRuns]}>{t('leadFinder.runs.empty')}</Text>}
      </ScrollView>
    );
  };

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications
          showMessages
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      formHeader={
        <FormHeader
          title={t('leadFinder.title')}
          subtitle={!loading && !locked ? usageLine() || (total === 1 ? t('leadFinder.subtitle.one') : t('leadFinder.subtitle.count', { count: total })) : undefined}
          onBack={goBack}
          actions={locked ? [] : [
            { icon: 'add', primary: true, onPress: () => { setForm(EMPTY_FORM); setAiPlanned(false); setShowForm(true); }, label: t('leadFinder.form.title') },
            { icon: 'refresh', onPress: () => { setRefreshing(true); loadRuns(); }, label: t('leadFinder.refresh') },
          ]}
        />
      }
    >
      <View style={styles.container}>{renderBody()}</View>
      <ZoneMapPicker
        visible={showMap}
        onClose={() => setShowMap(false)}
        onConfirm={onZone}
        initialMode="radius"
        title={t('leadFinder.map.title')}
        confirmLabel={t('leadFinder.map.confirm')}
        nameLabel={t('leadFinder.map.name')}
        hidePrice
      />
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.lightGray },
  content: { padding: spacing.md, paddingBottom: Platform.OS !== 'web' ? 100 : spacing.lg, gap: spacing.md, maxWidth: 960, width: '100%', alignSelf: 'center' },
  card: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    padding: spacing.md,
    gap: spacing.sm,
    ...designTokens.shadows.sm,
  },
  cardTitle: { fontSize: typography.fontSize.base, color: colors.text.primary, fontFamily: typography.fontFamily.semibold },
  sectionTitle: { fontSize: typography.fontSize.lg, color: colors.text.primary, fontFamily: typography.fontFamily.semibold, marginTop: spacing.sm },
  aiTitleRow: { alignItems: 'center', gap: spacing.sm },
  rowBetween: { alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm },
  wrapRow: { flexWrap: 'wrap', gap: spacing.xs },
  buttonRow: { gap: spacing.sm, flexWrap: 'wrap' },
  flexButton: { flexGrow: 1, minWidth: 160 },
  flex: { flex: 1 },
  label: { fontSize: typography.fontSize.sm, color: colors.text.primary, fontFamily: typography.fontFamily.semibold, marginTop: spacing.sm },
  hint: { fontSize: typography.fontSize.xs, color: colors.text.secondary, fontFamily: typography.fontFamily.regular },
  meta: { fontSize: typography.fontSize.xs, color: colors.text.secondary, fontFamily: typography.fontFamily.regular },
  input: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.regular,
    backgroundColor: colors.background.primary,
  },
  multiline: { minHeight: 84, textAlignVertical: 'top' },
  limitInput: { maxWidth: 140 },
  toggleRow: { marginTop: spacing.sm },
  linkButton: { alignSelf: 'flex-start', paddingVertical: spacing.xs },
  linkText: { fontSize: typography.fontSize.sm, color: colors.primary, fontFamily: typography.fontFamily.semibold },
  startButton: { marginTop: spacing.md },
  usageRow: { gap: 4 },
  runHeader: { alignItems: 'center', gap: spacing.sm },
  runIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: String(colors.primary) + '15',
    justifyContent: 'center',
    alignItems: 'center',
  },
  runStats: { marginTop: 2 },
  runActions: { gap: spacing.sm, marginTop: spacing.xs, paddingTop: spacing.sm, borderTopWidth: 1, borderTopColor: colors.border },
  runAction: {
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: designTokens.borderRadius.sm,
    backgroundColor: String(colors.primary) + '10',
  },
  runActionText: { fontSize: typography.fontSize.xs, color: colors.primary, fontFamily: typography.fontFamily.semibold },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xxl, gap: spacing.sm },
  centerButton: { minWidth: 160, marginTop: spacing.md },
  emptyTitle: { fontSize: typography.fontSize.xl, color: colors.text.primary, fontFamily: typography.fontFamily.semibold, textAlign: 'center' },
  emptyText: { fontSize: typography.fontSize.base, color: colors.text.secondary, fontFamily: typography.fontFamily.regular, textAlign: 'center' },
  emptyRuns: { paddingVertical: spacing.lg },
});
